export const FACILITY_OPTIONS = [
  "Bathroom",
  "Toilet",
  "AC",
  "Non-AC",
  "Changing Room",
  "Makeup Room",
  "Waiting Area",
  "Soundproof",
  "Fitness Center",
  "Swimming Pool",
  "Garden",
  "Elevator",
  "Wheelchair Accessible",
  "Dining Space",
];

export const EVENT_TYPE_OPTIONS = ["Wedding", "Party", "Corporate", "Shoot", "Others"];

export const FURNISHING_OPTIONS = ["Unfurnished", "Semi-Furnished", "Furnished"];

export const FACING_OPTIONS = ["North", "South", "East", "West"];

export const OWNERSHIP_OPTIONS = ["Owned", "Rented", "Managed"];

export const NETWORK_COVERAGE_OPTIONS = ["Poor", "Average", "Good", "Excellent"];

export const POWER_BACKUP_TYPES = ["Generator", "Inverter"];

export function getDefaultLocationDetails() {
  return {
    amenities: {
      powerBackup: false,
      powerBackupTypes: [],
      electricPowerAvailable: false,
      electricPerUnitCost: "",
      waterSupply: false,
      wifi: false,
      cctv: false,
      security24x7: false,
    },
    facilities: [],
    permissions: {
      locationPermission: false,
      noc_certificate: null,
      business_license: null,
      fire_safety_certificate: null,
      local_authority_permission: null,
      insurance: null,
      dronePermission: false,
      permissionRequired: true,
    },
    space: {
      totalAreaSqFt: "",
      seatingCapacity: "",
      standingCapacity: "",
      furnishingType: "",
      numberOfRooms: "",
      stayAvailable: false,
      floorNumber: "",
      totalFloors: "",
      facingDirection: "",
      ownershipType: "",
    },
    pricing: {
      securityDeposit: "",
    },
    media: {
      videoLink: "",
    },
    rules: {
      smokingAllowed: false,
      alcoholAllowed: false,
      loudMusicAllowed: false,
      petsAllowed: false,
      closingTime: "",
      mobileNetworkCoverage: "",
      eventTypesAllowed: [],
    },
    parking: {
      parking2Wheeler: "",
      parking4Wheeler: "",
      parking8Wheeler: "",
      helipadAvailable: false,
      privateAirRunway: false,
    },
  };
}

/**
 * @param {unknown} raw
 * @returns {object}
 */
export function normalizeLocationDetailsFromApi(raw) {
  const base = getDefaultLocationDetails();
  if (raw == null) return base;
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return base;
    }
  }
  if (typeof obj !== "object" || !obj) return base;
  return {
    amenities: { ...base.amenities, ...(obj.amenities || {}) },
    facilities: Array.isArray(obj.facilities) ? [...obj.facilities] : [],
    permissions: { ...base.permissions, ...(obj.permissions || {}) },
    space: { ...base.space, ...(obj.space || {}) },
    pricing: { ...base.pricing, ...(obj.pricing || {}) },
    media: { ...base.media, ...(obj.media || {}) },
    rules: { ...base.rules, ...(obj.rules || {}) },
    parking: { ...base.parking, ...(obj.parking || {}) },
  };
}

export function isValidYoutubeOrVimeo(url) {
  if (!url || !String(url).trim()) return true;
  try {
    const h = new URL(String(url).trim()).hostname.replace(/^www\./i, "");
    return /youtube\.com|youtu\.be|vimeo\.com/i.test(h);
  } catch {
    return false;
  }
}

/**
 * Client-side validation before submit (server validates too).
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateLocationDetailsClient(loc, tokenAmount) {
  if (!loc) return { ok: true };
  const token = Number(tokenAmount);
  const dep = loc.pricing?.securityDeposit;
  const deposit = dep === "" || dep == null ? NaN : Number(dep);
  if (Number.isFinite(token) && token > 0 && Number.isFinite(deposit) && deposit < token) {
    return {
      ok: false,
      message: `Security deposit must be at least the listing token amount (₹${token}).`,
    };
  }
  const perm = loc.permissions || {};
  if (perm.permissionRequired !== false && perm.locationPermission === true) {
    if (!perm.noc_certificate) {
      return {
        ok: false,
        message: "Upload NOC certificate (PDF) when location permission is Yes.",
      };
    }
  }
  if (loc.media?.videoLink && !isValidYoutubeOrVimeo(loc.media.videoLink)) {
    return { ok: false, message: "Video link must be YouTube or Vimeo." };
  }
  return { ok: true };
}

export function countLocationSectionsProgress(loc) {
  if (!loc) return 0;
  let n = 0;
  const a = loc.amenities || {};
  if (a.powerBackup || a.electricPowerAvailable || a.waterSupply || a.wifi || a.cctv || a.security24x7) n++;
  if ((loc.facilities || []).length) n++;
  const p = loc.permissions || {};
  if (
    p.locationPermission ||
    p.dronePermission ||
    p.noc_certificate ||
    p.business_license ||
    p.fire_safety_certificate
  )
    n++;
  const s = loc.space || {};
  if (Object.values(s).some((v) => v !== "" && v !== false)) n++;
  const pr = loc.pricing || {};
  if (pr.securityDeposit !== "" && pr.securityDeposit != null) n++;
  if (loc.media?.videoLink) n++;
  const r = loc.rules || {};
  if (
    r.smokingAllowed ||
    r.alcoholAllowed ||
    r.loudMusicAllowed ||
    r.petsAllowed ||
    r.closingTime ||
    r.mobileNetworkCoverage ||
    (r.eventTypesAllowed || []).length
  )
    n++;
  const pk = loc.parking || {};
  if (
    pk.parking2Wheeler ||
    pk.parking4Wheeler ||
    pk.parking8Wheeler ||
    pk.helipadAvailable ||
    pk.privateAirRunway
  )
    n++;
  return Math.min(n, 8);
}

export function getListingTokenAmountFromStore(store) {
  if (!store) return 0;
  let po = store.planObject;
  let pd = store.paymentDetails;
  if (typeof po === "string") {
    try {
      po = JSON.parse(po || "{}");
    } catch {
      po = {};
    }
  }
  if (typeof pd === "string") {
    try {
      pd = JSON.parse(pd || "{}");
    } catch {
      pd = {};
    }
  }
  const t = po?.token_amount ?? pd?.token_amount ?? po?.tokenAmount ?? pd?.tokenAmount;
  const n = Number(t);
  return Number.isFinite(n) ? n : 0;
}

export function hasRenderableLocationDetails(product) {
  const raw = product?.location_details;
  if (raw == null) return false;
  const loc = normalizeLocationDetailsFromApi(raw);
  return countLocationSectionsProgress(loc) > 0;
}
